/**
 * PresetManager — Orchestration layer for named scene presets.
 *
 * A preset bundles environment + IBL + sunlight + shadow into one named
 * combination. Calling `apply('road')` sets all four subsystems at once,
 * derived from the matching EnvironmentDescriptor.
 *
 * Individual `setEnvironment()`, `setIBL()`, `setSunlight()`, `setShadow()`
 * calls still work for fine-tuning after a preset is applied — each system
 * stands alone, and the preset is the only cross-cutting orchestrator.
 *
 * Built-in presets are derived from ENVIRONMENT_DESCRIPTORS. Custom presets
 * can be registered at runtime via `register()`.
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 * @see docs/ims-engine-api/01-architecture.md
 */

import type { EnvironmentManager } from './EnvironmentManager';
import type { IBLManager } from './IBLManager';
import type { SunlightManager } from '../core/SunlightManager';
import type { GroundShadowManager } from '../core/GroundShadowManager';
import { ENVIRONMENT_IDS } from './EnvironmentDescriptors';
import type { ShadowMode, EnvironmentId } from '../api/types';

// ---------------------------------------------------------------------------
// PresetDescriptor — data shape for a named preset
// ---------------------------------------------------------------------------

/** Describes a named preset that orchestrates all subsystems at once. */
export interface PresetDescriptor {
  /** Unique preset identifier. */
  id: string;
  /** Environment ID to switch to. */
  environment?: string;
  /** IBL settings to apply. */
  ibl?: { hdrFile?: string; intensity?: number };
  /** Sunlight preset name or direct config. */
  sunlight?: string;
  /** Shadow mode to apply. */
  shadow?: ShadowMode;
}

// ---------------------------------------------------------------------------
// PresetManager
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Built-in preset defaults — one entry per EnvironmentId
// ---------------------------------------------------------------------------
// These define what IBL / sunlight / shadow each preset applies.
// The visual scene (background, ground, fog) comes from the EnvironmentDescriptor.
// The data lives HERE, not in the descriptor, because setEnvironment() must
// never touch IBL, sunlight, or shadow.
// ---------------------------------------------------------------------------

export const BUILT_IN_PRESET_DEFAULTS: Record<EnvironmentId, Omit<PresetDescriptor, 'id' | 'environment'>> = {
  spotlight:   { ibl: { hdrFile: 'studio.hdr', intensity: 1.0 }, sunlight: 'dramatic',       shadow: 'contact' },
  studio:      { ibl: { hdrFile: 'studio.hdr', intensity: 1.0 }, sunlight: 'studio-neutral', shadow: 'contact' },
  hdri:        { ibl: { hdrFile: 'studio.hdr', intensity: 1.0 }, sunlight: 'studio-neutral', shadow: 'contact' },
  road:        { ibl: { hdrFile: 'sunset.hdr', intensity: 1.0 }, sunlight: 'golden-hour',    shadow: 'contact' },
  transparent: { ibl: { hdrFile: 'studio.hdr', intensity: 1.0 }, sunlight: 'studio-neutral', shadow: 'baked' },
};

export class PresetManager {
  private _envManager:      EnvironmentManager;
  private _iblManager:      IBLManager;
  private _sunlightManager: SunlightManager;
  private _shadowManager:   GroundShadowManager;

  /** Custom presets registered at runtime (overrides built-in). */
  private _custom = new Map<string, PresetDescriptor>();

  constructor(
    envManager:      EnvironmentManager,
    iblManager:      IBLManager,
    sunlightManager: SunlightManager,
    shadowManager:   GroundShadowManager,
  ) {
    this._envManager      = envManager;
    this._iblManager      = iblManager;
    this._sunlightManager = sunlightManager;
    this._shadowManager   = shadowManager;
  }

  // ---------------------------------------------------------------------------
  // apply — main entry point
  // ---------------------------------------------------------------------------

  /**
   * Apply a named preset — orchestrates all subsystems from one call.
   *
   * Built-in presets are derived from `ENVIRONMENT_DESCRIPTORS`: each
   * environment ID ('spotlight', 'studio', 'hdri', 'road') is also a preset.
   *
   * @example
   * manager.apply('road')     // env=road, ibl=sunset.hdr, sun=golden-hour, shadow=contact
   * manager.apply('spotlight') // env=spotlight, ibl=studio.hdr, sun=dramatic, shadow=contact
   */
  apply(id: string): void {
    // Check custom presets first
    const custom = this._custom.get(id);
    if (custom) {
      this._applyDescriptor(custom);
      return;
    }

    // Fall back to built-in preset defaults
    const defaults = BUILT_IN_PRESET_DEFAULTS[id as EnvironmentId];
    if (!defaults) {
      console.warn(`[PresetManager] Unknown preset "${id}".`);
      return;
    }

    // Combine environment switch + independent subsystem defaults
    this._applyDescriptor({
      id,
      environment: id,
      ...defaults,
    });
  }

  // ---------------------------------------------------------------------------
  // register — custom presets
  // ---------------------------------------------------------------------------

  /**
   * Register a custom preset. Overrides built-in presets with the same ID.
   *
   * @example
   * manager.register({
   *   id: 'golden-road',
   *   environment: 'road',
   *   ibl: { hdrFile: 'sunset.hdr', intensity: 1.2 },
   *   sunlight: 'golden-hour',
   *   shadow: 'contact',
   * });
   */
  register(preset: PresetDescriptor): void {
    this._custom.set(preset.id, preset);
  }

  // ---------------------------------------------------------------------------
  // Query
  // ---------------------------------------------------------------------------

  /** Returns all available preset IDs (built-in + custom). */
  getPresetIds(): string[] {
    const builtIn = ENVIRONMENT_IDS as readonly string[];
    const customIds = [...this._custom.keys()];
    // Deduplicate — custom overrides win
    const set = new Set([...builtIn, ...customIds]);
    return [...set];
  }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    this._custom.clear();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private _applyDescriptor(preset: PresetDescriptor): void {
    if (preset.environment) this._envManager.apply(preset.environment);
    if (preset.ibl)         this._iblManager.apply(preset.ibl);
    if (preset.sunlight)    this._sunlightManager.apply(preset.sunlight);
    if (preset.shadow)      this._shadowManager.apply(preset.shadow);
  }
}
